import { createContext, useContext, useState, useCallback, ReactNode, useEffect, useRef } from 'react';
import type { AppContextType, User, ProfileData, Skill, AssessmentResult, IncompleteAssessmentSession } from '../types/context.types';
import type { FluencyProfileResponse, PaidStatusResponse, Role, UserTier } from '../types/api.types';
import api from '../services/api';
import { useLocalStorage } from '../hooks/useLocalStorage';
import { authService } from '../services/authService';
import { identifyUser, resetUser, trackAppError } from '../lib/analytics';
import { reportError } from '../lib/errorManager';
import { setSentryUser, clearSentryUser } from '../lib/sentry';
import { storeLoginReturnUrl } from '../lib/loginReturnUrl';
import {
  consumeAcademyAuthResync,
  shouldAcademyAuthResync,
} from '../lib/academyAuthResync';
import { clearStudioRedirectShell } from '../lib/studioRedirectShell';
import { markLoggedOut, clearLoggedOut, consumeLoggedOut } from '../lib/loggedOutFlag';
import { resetCertificateClaimToastStorage } from '../lib/certificateClaimToastStorage';

const SESSION_CHECK_TIMEOUT_MS = 12000;

const AppContext = createContext<AppContextType | undefined>(undefined);

interface AppProviderProps {
  children: ReactNode;
}

export function AppProvider({ children }: AppProviderProps) {
  const [user, setUser] = useState<User | null>(null);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [loading, setLoading] = useState(true);
  const [sessionConfirmed, setSessionConfirmed] = useState(false);

  // Use the useLocalStorage hook for all persisted state
  const [profileData, setProfileData] = useLocalStorage<ProfileData | null>('profileData', null);
  const [skills, setSkills] = useLocalStorage<Skill[]>('skills', []);
  const [assessmentResults, setAssessmentResults] = useLocalStorage<AssessmentResult[] | null>('assessmentResults', null);
  const [advancedResults, setAdvancedResults] = useLocalStorage<any>('advancedResults', null);
  const [advancedSessionId, setAdvancedSessionId] = useLocalStorage<number | null>('advancedSessionId', null);
  const [upskillPlan, setUpskillPlan] = useLocalStorage<any>('upskillPlan', null);
  const [apiProfile, setApiProfile] = useLocalStorage<FluencyProfileResponse | null>('apiProfile', null);
  const [incompleteAssessment, setIncompleteAssessment] = useLocalStorage<IncompleteAssessmentSession | null>('incompleteAssessment', null);
  const [roles, setRoles] = useLocalStorage<Role[]>('roles', []);
  const [paidStatus, setPaidStatus] = useLocalStorage<PaidStatusResponse | null>('paidStatus', null);
  const [userTier, setUserTier] = useLocalStorage<UserTier | null>('userTier', null);

  const [rolesLoading, setRolesLoading] = useState(false);
  const [isLoginModalOpen, setIsLoginModalOpen] = useState(false);
  const [loginModalMessage, setLoginModalMessage] = useState<string | null>(null);
  const [loginModalInitialAuthMode, setLoginModalInitialAuthMode] = useState<'signin' | 'signup' | undefined>(undefined);
  const [isVerifyEmailModalOpen, setIsVerifyEmailModalOpen] = useState(false);
  // Whether the modal should auto-send a fresh OTP on open. True when opened from
  // the nudge banner (a returning user has no code yet); false right after signup,
  // where the signup request already issued one.
  const [verifyEmailAutoSend, setVerifyEmailAutoSend] = useState(false);

  const isPaid = paidStatus?.is_paid ?? false;
  // `=== false` (not falsy) is load-bearing: an `undefined` emailVerified means the
  // field is unknown (e.g. older session response) and must NOT trigger the nudge —
  // only an explicit `false` from the API does.
  const needsEmailVerification = !!user && user.provider === 'email' && user.emailVerified === false;

  const refreshPaidStatus = useCallback(async (): Promise<PaidStatusResponse | null> => {
    try {
      const response = await api.get('/v1/auth/paid-status');
      if (response.data.status === 'success') {
        const data = response.data.data;
        setPaidStatus(data);
        if (data.tier) {
          setUserTier(data.tier);
        }
        return data;
      }
    } catch (error) {
      reportError(error, { source: 'AppContext', operation: 'refreshPaidStatus' });
    }
    return null;
  }, [setPaidStatus, setUserTier]);

  const clearAuthState = useCallback(() => {
    setUser(null);
    setIsLoggedIn(false);
    setUserTier(null);
    setPaidStatus(null);
  }, [setPaidStatus, setUserTier]);

  const refreshSession = useCallback(async (): Promise<User | null> => {
    try {
      const response = await api.get('/v1/auth/me', { timeout: SESSION_CHECK_TIMEOUT_MS });
      if (response.data.status === 'success') {
        const sessionUser = response.data.data.user as User;
        setUser(sessionUser);
        setIsLoggedIn(true);
        if (response.data.data.tier) {
          setUserTier(response.data.data.tier);
        }
        void refreshPaidStatus();
        return sessionUser;
      }
    } catch (error) {
      const status = (error as { response?: { status?: number } }).response?.status;
      if (status === 401) {
        clearAuthState();
      } else {
        reportError(error, { source: 'AppContext', operation: 'refreshSession' });
      }
    }
    return null;
  }, [clearAuthState, refreshPaidStatus, setUserTier]);

  const resyncAuthFromServer = useCallback(async (): Promise<void> => {
    setSessionConfirmed(false);
    const sessionUser = await refreshSession();
    if (!sessionUser) {
      clearAuthState();
    }
    setSessionConfirmed(true);
  }, [clearAuthState, refreshSession]);

  const userTierRef = useRef(userTier);
  userTierRef.current = userTier;

  useEffect(() => {
    // If the session-check network calls hang in "pending" forever (flaky mobile
    // networks, request swallowed mid-flight), the global `loading` flag never
    // flips and every page that gates on it shows only a spinner. Fail open:
    // after this budget, treat the user as anonymous and let the UI render.
    // After bailout, ignore any late-arriving response so we don't silently
    // flip the user from anonymous → authenticated mid-interaction.
    let bailedOut = false;
    const timeoutId = window.setTimeout(() => {
      bailedOut = true;
      setLoading(prev => {
        if (prev) {
          trackAppError({
            error_type: 'session_check_timeout',
            error_message: `AppContext.checkSession exceeded ${SESSION_CHECK_TIMEOUT_MS}ms`,
          });
        }
        return false;
      });
    }, SESSION_CHECK_TIMEOUT_MS);

    const checkSession = async () => {
      // After an explicit logout, skip session restoration so the user
      // isn't immediately redirected back to an authenticated route.
      if (consumeLoggedOut()) {
        window.clearTimeout(timeoutId);
        setLoading(false);
        setSessionConfirmed(true);
        return;
      }

      if (consumeAcademyAuthResync()) {
        clearAuthState();
        await resyncAuthFromServer();
        if (bailedOut) return;
        window.clearTimeout(timeoutId);
        if (!bailedOut) {
          setLoading(false);
        }
        return;
      }

      const sessionUser = await refreshSession();
      if (bailedOut) return;
      if (!sessionUser && userTierRef.current === 'guest') {
        // Session cookie expired — re-create guest session (no email; backend mints a fresh user)
        const restored = await authService.createGuestSession();
        if (bailedOut) return;
        if (restored.ok) {
          setUser({
            name: restored.name ?? 'Guest',
            email: restored.email ?? null,
            provider: 'guest',
          });
          setIsLoggedIn(true);
        } else {
          // Clear stale state so downstream components can create fresh session
          setUserTier(null);
          setIsLoggedIn(false);
        }
      }

      window.clearTimeout(timeoutId);
      if (!bailedOut) {
        setLoading(false);
        setSessionConfirmed(true);
      }
    };

    const fetchRoles = async (cachedRoles: Role[]) => {
      if (cachedRoles.length === 0) {
        setRolesLoading(true);
        try {
          const rolesResponse = await api.get('/v1/questions/roles');
          if (rolesResponse.data.status === 'success') {
            setRoles(rolesResponse.data.data.roles);
          }
        } catch (error) {
          reportError(error, { source: 'AppContext', operation: 'fetchRoles' });
        } finally {
          setRolesLoading(false);
        }
      }
    };

    checkSession();
    fetchRoles(roles);

    return () => window.clearTimeout(timeoutId);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- mount-only session bootstrap
  }, []);

  // Freeze a logged-out navbar snapshot before Studio navigation is cached (bfcache),
  // then re-fetch auth when the user returns via Back.
  useEffect(() => {
    const runResyncIfNeeded = (): void => {
      if (!shouldAcademyAuthResync()) return;
      consumeAcademyAuthResync();
      clearAuthState();
      void resyncAuthFromServer();
    };

    const onPageHide = (event: PageTransitionEvent): void => {
      if (!event.persisted || !shouldAcademyAuthResync()) return;
      clearAuthState();
    };

    const onPageShow = (event: PageTransitionEvent): void => {
      if (event.persisted) {
        clearStudioRedirectShell();
      }
      runResyncIfNeeded();
    };

    const onVisibilityChange = (): void => {
      if (document.visibilityState === 'visible') {
        runResyncIfNeeded();
      }
    };

    window.addEventListener('pagehide', onPageHide);
    window.addEventListener('pageshow', onPageShow);
    document.addEventListener('visibilitychange', onVisibilityChange);
    return () => {
      window.removeEventListener('pagehide', onPageHide);
      window.removeEventListener('pageshow', onPageShow);
      document.removeEventListener('visibilitychange', onVisibilityChange);
    };
  }, [clearAuthState, resyncAuthFromServer]);

  useEffect(() => {
    if (!user) return;
    const plan = paidStatus?.tier ?? userTier ?? undefined;
    identifyUser(user, plan ? { plan } : undefined);
    setSentryUser({ id: user.loginId });
  }, [user, userTier, paidStatus?.tier]);

  const login = useCallback(async (_userData?: User) => {
    // Clear the post-logout guard so a reload after re-login doesn't skip `/me`
    // and wrongly show the user as logged out (and suppress nudges). See [[loggedOutFlag]].
    clearLoggedOut();
    setSessionConfirmed(false);
    await refreshSession();
    setSessionConfirmed(true);
  }, [refreshSession]);

  const logout = async () => {
    try {
      await api.post('/v1/auth/logout');
    } catch (error) {
      reportError(error, { source: 'AppContext', operation: 'logout' });
    } finally {
      resetUser();
      clearSentryUser();
      resetCertificateClaimToastStorage();
      localStorage.clear();
      markLoggedOut();

      // Reset auth state
      setUser(null);
      setIsLoggedIn(false);

      // Reset all useLocalStorage hook states to their defaults
      setUserTier(null);
      setProfileData(null);
      setSkills([]);
      setAssessmentResults(null);
      setAdvancedResults(null);
      setAdvancedSessionId(null);
      setUpskillPlan(null);
      setApiProfile(null);
      setIncompleteAssessment(null);
      setPaidStatus(null);
      // Note: roles is not reset — non-user-specific, safe to retain
    }
  };

  const openLoginModal = useCallback((successMessage?: string, returnUrl?: string, initialAuthMode?: 'signin' | 'signup') => {
    storeLoginReturnUrl(returnUrl ?? window.location.pathname);
    setLoginModalMessage(successMessage || null);
    setLoginModalInitialAuthMode(initialAuthMode);
    setIsLoginModalOpen(true);
  }, []);

  const closeLoginModal = useCallback(() => {
    setIsLoginModalOpen(false);
    setLoginModalMessage(null);
    setLoginModalInitialAuthMode(undefined);
  }, []);

  const openVerifyEmailModal = useCallback((opts?: { autoSend?: boolean }) => {
    setVerifyEmailAutoSend(opts?.autoSend ?? false);
    setIsVerifyEmailModalOpen(true);
  }, []);
  const closeVerifyEmailModal = useCallback(() => {
    setIsVerifyEmailModalOpen(false);
    setVerifyEmailAutoSend(false);
  }, []);

  const markUserEmailVerified = useCallback(() => {
    setUser(prev => (prev ? { ...prev, emailVerified: true } : prev));
  }, []);

  const createGuestSession = useCallback(async (opts?: { email?: string; name?: string }) => {
    const result = await authService.createGuestSession(opts);
    if (result.ok) {
      // Guest sessions are a login path too — clear the post-logout guard so a
      // later reload restores the guest instead of skipping `/me`.
      clearLoggedOut();
      setSessionConfirmed(false);
      await refreshSession();
      setSessionConfirmed(true);
    }
    return result;
  }, [refreshSession]);

  return (
    <AppContext.Provider value={{
      user,
      isLoggedIn,
      loading,
      login,
      logout,
      profileData,
      setProfileData,
      skills,
      setSkills,
      assessmentResults,
      setAssessmentResults,
      advancedResults,
      setAdvancedResults,
      advancedSessionId,
      setAdvancedSessionId,
      upskillPlan,
      setUpskillPlan,
      apiProfile,
      setApiProfile,
      incompleteAssessment,
      setIncompleteAssessment,
      roles,
      rolesLoading,
      isPaid,
      refreshPaidStatus,
      refreshSession,
      sessionConfirmed,
      userTier,
      createGuestSession,
      isLoginModalOpen,
      loginModalMessage,
      loginModalInitialAuthMode,
      openLoginModal,
      closeLoginModal,
      needsEmailVerification,
      isVerifyEmailModalOpen,
      verifyEmailAutoSend,
      openVerifyEmailModal,
      closeVerifyEmailModal,
      markUserEmailVerified
    }}>
      {children}
    </AppContext.Provider>
  );
}

export function useApp(): AppContextType {
  const context = useContext(AppContext);
  if (!context) {
    throw new Error('useApp must be used within AppProvider');
  }
  return context;
}
